<template>
  <WdsStack direction-column gap-lg>
    <div v-if="jobLoadingError" class="tw-mt-9">
      <ContentLoadingErrorNotice />
    </div>

    <template v-else>
      <div v-if="!loading && job">
        <FeatureFlagged name="job-candidates-v-1-2">
          <div class="tw-mb-8">
            <TalentChannelsSummary
              :company-id="companyId"
              :job-id="jobId"
              :is-published="jobObj?.published"
              :is-available="jobObj?.available"
              :is-for-marketplace="jobObj?.isForMarketplace"
              :count-staffing-agency-shares="jobObj?.recruiterShares?.paginatorInfo?.total ?? 0"
              :viewer-can-share="jobObj?.viewerCanShare ?? false"
              @contacts-updated="handleContactsUpdated"
            />
          </div>
        </FeatureFlagged>

        <CandidatesSection :job="job" />

        <FeatureFlagged name="job-candidates-v-1-2">
          <template #is-disabled>
            <CollaboratorsSection :job="job" @contacts-updated="handleContactsUpdated" />
          </template>
        </FeatureFlagged>
      </div>

      <div v-else class="tw-flex tw-mt-6 tw-justify-center">
        <WdsSpinner :size="40" indeterminate />
      </div>
    </template>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { WdsSpinner, WdsStack } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'

import { FragmentType, useFragment } from '@/gql'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'

import { GetJobQuery } from '@/components/Job/graphql/queries'
import { JobFragment } from '@/components/Job/graphql/fragments'

import CollaboratorsSection from '@/components/Job/Candidates/CollaboratorsSection.vue'
import CandidatesSection from '@/components/Job/Candidates/CandidatesSection.vue'
import TalentChannelsSummary from '@/components/Job/Candidates/TalentChannelsSummary.vue'

const props = defineProps<{
  companyId: string
  jobId: string
}>()

const {
  result: jobResult,
  loading,
  onError: onJobLoadingError,
} = useQuery(
  GetJobQuery,
  {
    jobId: props.jobId,
  },
  { clientId: 'publicApi' },
)

const jobLoadingError = ref(false)
const jobObj = ref(useFragment(JobFragment, null))

const job = computed(() => jobResult.value?.job)

watch(
  () => job.value,
  () => {
    jobObj.value = useFragment(JobFragment, job.value) ?? null
  },
)

const getJob = () =>
  publicApi.readFragment({
    id: `Job:${props.jobId}`,
    fragment: JobFragment,
  })

const handleContactsUpdated = () => {
  const jobFromCache = getJob() as FragmentType<typeof JobFragment>
  jobObj.value = useFragment(JobFragment, jobFromCache)
}

onJobLoadingError((error) => {
  jobLoadingError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})
</script>
